import { useState, useEffect, useRef, useCallback } from "react";

const servicos = [
  {
    icon: "ri-file-chart-line",
    title: "Planejamento Previdenciário",
    desc: "Orientação estratégica para maximizar seu benefício futuro, analisando contribuições e o melhor momento para se aposentar.",
  },
  {
    icon: "ri-user-star-line",
    title: "Aposentadorias",
    desc: "Por idade, tempo de contribuição, invalidez ou especial. Analisamos o melhor caminho para você se aposentar com o maior benefício possível.",
  },
  {
    icon: "ri-hand-heart-line",
    title: "LOAS / BPC",
    desc: "Benefício de Prestação Continuada para idosos e pessoas com deficiência de baixa renda. Sem precisar ter contribuído ao INSS.",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Pensão por Morte",
    desc: "Garantimos o direito da família ao receber pensão após o falecimento do segurado, com todo o suporte necessário.",
  },
  {
    icon: "ri-women-line",
    title: "Salário-Maternidade",
    desc: "Para trabalhadoras urbanas, rurais e domésticas. Garantimos seu direito ao salário-maternidade durante a licença.",
  },
  {
    icon: "ri-hospital-line",
    title: "Auxílio-Doença",
    desc: "Quando você fica incapacitado para o trabalho por doença ou acidente, garantimos seu auxílio junto ao INSS.",
  },
  {
    icon: "ri-first-aid-kit-line",
    title: "Auxílio-Acidente",
    desc: "Para quem sofreu acidente e ficou com sequelas permanentes. Você tem direito a indenização mesmo trabalhando.",
  },
];

const CARDS_PER_VIEW_DESKTOP = 3;
const CARDS_PER_VIEW_TABLET = 2;
const CARDS_PER_VIEW_MOBILE = 1;

function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

export default function ServicosSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const width = useWindowWidth();

  const cardsPerView =
    width >= 1024 ? CARDS_PER_VIEW_DESKTOP :
    width >= 640 ? CARDS_PER_VIEW_TABLET :
    CARDS_PER_VIEW_MOBILE;

  const maxIndex = Math.max(0, servicos.length - cardsPerView);

  const goTo = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating, maxIndex]);

  const prev = () => goTo(currentIndex - 1);
  const next = () => goTo(currentIndex + 1);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = "1";
            (entry.target as HTMLElement).style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll(".reveal-servicos");
    elements?.forEach((el) => {
      (el as HTMLElement).style.opacity = "0";
      (el as HTMLElement).style.transform = "translateY(30px)";
      (el as HTMLElement).style.transition = "opacity 0.7s ease, transform 0.7s ease";
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const translateX = -(currentIndex * (100 / cardsPerView));

  return (
    <section
      id="servicos"
      ref={sectionRef}
      className="py-20 md:py-28 overflow-hidden"
      style={{ backgroundColor: "#F5F5F5" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="reveal-servicos text-center mb-16">
          <span
            className="inline-block text-sm font-semibold tracking-widest uppercase mb-3"
            style={{ fontFamily: "Montserrat, sans-serif", color: "#0B4D6F" }}
          >
            O Que Fazemos
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            style={{ fontFamily: "Cinzel, serif", color: "#1A1A1A" }}
          >
            Nossos Serviços
          </h2>
          <div className="mx-auto mb-4" style={{ width: "60px", height: "3px", backgroundColor: "#0B4D6F", borderRadius: "2px" }} />
          <p
            className="text-base md:text-lg max-w-2xl mx-auto"
            style={{ fontFamily: "Montserrat, sans-serif", color: "#666" }}
          >
            Soluções completas para garantir seus direitos previdenciários junto ao INSS
          </p>
        </div>

        {/* Carousel */}
        <div className="reveal-servicos relative">
          {/* Prev Arrow */}
          <button
            onClick={prev}
            disabled={currentIndex === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 z-10 w-11 h-11 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110"
            style={{ backgroundColor: "#FFFFFF", border: "2px solid #0B4D6F", color: "#0B4D6F" }}
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>

          {/* Cards Track */}
          <div className="overflow-hidden mx-6 md:mx-8">
            <div
              className="flex"
              style={{
                transform: `translateX(${translateX}%)`,
                transition: "transform 0.4s ease-in-out",
              }}
            >
              {servicos.map((servico, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 px-3"
                  style={{ width: `${100 / cardsPerView}%` }}
                >
                  <div
                    className="bg-white rounded-2xl p-8 h-full flex flex-col cursor-default transition-all duration-300 hover:-translate-y-1"
                    style={{
                      border: "1px solid #E5E5E5",
                      minHeight: "280px",
                    }}
                  >
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-6 flex-shrink-0"
                      style={{ backgroundColor: "#EBF4F8" }}
                    >
                      <i className={`${servico.icon} text-2xl`} style={{ color: "#0B4D6F" }}></i>
                    </div>
                    <h3
                      className="text-xl font-semibold mb-3"
                      style={{ fontFamily: "Cinzel, serif", color: "#1A1A1A" }}
                    >
                      {servico.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed flex-1"
                      style={{ fontFamily: "Montserrat, sans-serif", color: "#666" }}
                    >
                      {servico.desc}
                    </p>
                    <a
                      href="https://wa.me/5598992018216?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20meus%20direitos%20previdenci%C3%A1rios."
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1 mt-5 text-sm font-semibold cursor-pointer hover:gap-2 transition-all duration-200"
                      style={{ fontFamily: "Montserrat, sans-serif", color: "#0B4D6F" }}
                    >
                      Saiba mais <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Arrow */}
          <button
            onClick={next}
            disabled={currentIndex >= maxIndex}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 z-10 w-11 h-11 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110"
            style={{ backgroundColor: "#FFFFFF", border: "2px solid #0B4D6F", color: "#0B4D6F" }}
            aria-label="Próximo"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="rounded-full cursor-pointer transition-all duration-300"
              style={{
                width: currentIndex === i ? "24px" : "8px",
                height: "8px",
                backgroundColor: currentIndex === i ? "#0B4D6F" : "#CBD5E0",
              }}
              aria-label={`Ir para slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
